import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetSaleListingDetailQuery,
    useProcessPurchaseRequestMutation
} from '../../../../api/endpoints/vehicleSale';
import { toastService } from '../../../../utils/toastService';
import {
    getListingStatusSeverity,
    getRequestStatusSeverity,
    REQUEST_STATUS,
    REQUEST_ACTION
} from '../utils/statusBadges';
import RejectRequestDialog from './RejectRequestDialog';

const ListingDetailDialog = ({ visible, listingId, onHide, onActionComplete }) => {
    const { th, getCurrentLocale, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [rejectTarget, setRejectTarget] = useState(null);
    const [processRequest, { isLoading: isProcessing }] = useProcessPurchaseRequestMutation();

    const {
        data: detail,
        isFetching,
        refetch
    } = useGetSaleListingDetailQuery({ listingId, languageId }, { skip: !visible || !listingId });

    const formatDateTime = (value) => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleString(getCurrentLocale(), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return value;
        }
    };

    const handleApprove = (request) => {
        confirmDialog({
            header: th('vehiclesForSale.dialog.confirmApproveTitle'),
            message: th('vehiclesForSale.dialog.confirmApproveMessage'),
            icon: 'pi pi-question-circle',
            acceptLabel: th('vehiclesForSale.buttons.approveRequest'),
            rejectLabel: th('vehiclesForSale.buttons.cancel'),
            accept: async () => {
                try {
                    const res = await processRequest({
                        requestId: request.requestId,
                        action: REQUEST_ACTION.APPROVE,
                        languageId
                    }).unwrap();
                    if (res?.isSuccess || res?.isSuccessful) {
                        toastService.success(
                            res.message || th('vehiclesForSale.toast.requestApproved')
                        );
                        refetch();
                        onActionComplete?.();
                    }
                } catch (err) {
                    void err;
                }
            }
        });
    };

    const handleRejectOpen = (request) => setRejectTarget(request);
    const handleRejectClose = () => setRejectTarget(null);
    const handleRejectSuccess = () => {
        refetch();
        onActionComplete?.();
    };

    const infoRow = (label, value) => (
        <div className="col-12 md:col-6">
            <div className="text-xs text-500 mb-1">{label}</div>
            <div className="text-sm font-medium">{value || '-'}</div>
        </div>
    );

    const renderRequestCard = (req) => {
        const isPending = req.status === REQUEST_STATUS.PENDING;
        return (
            <div
                key={req.requestId}
                className="p-3 border-1 border-round surface-border mb-2"
            >
                <div className="flex justify-content-between align-items-start mb-2">
                    <div>
                        <div className="font-medium">{req.buyerBranchName}</div>
                        <div className="text-xs text-500">
                            {req.createdName} · {formatDateTime(req.createdDate)}
                        </div>
                    </div>
                    <Tag
                        value={req.statusText}
                        severity={getRequestStatusSeverity(req.status)}
                    />
                </div>

                {req.buyerMessage && (
                    <div className="mb-2">
                        <div className="text-xs text-500 mb-1">
                            {th('vehiclesForSale.labels.buyerMessage')}
                        </div>
                        <div className="text-sm">{req.buyerMessage}</div>
                    </div>
                )}

                {req.sellerResponseMessage && (
                    <div className="mb-2">
                        <div className="text-xs text-500 mb-1">
                            {th('vehiclesForSale.labels.sellerResponseMessage')}
                        </div>
                        <div className="text-sm">{req.sellerResponseMessage}</div>
                    </div>
                )}

                {req.decidedDate && (
                    <div className="text-xs text-500">
                        {th('vehiclesForSale.columns.decisionDate')}: {formatDateTime(req.decidedDate)}
                        {req.decidedName ? ` · ${req.decidedName}` : ''}
                    </div>
                )}

                {isPending && detail?.isOwnListing && (
                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label={th('vehiclesForSale.buttons.rejectRequest')}
                            icon="pi pi-ban"
                            size="small"
                            severity="danger"
                            outlined
                            onClick={() => handleRejectOpen(req)}
                            disabled={isProcessing}
                        />
                        <Button
                            label={th('vehiclesForSale.buttons.approveRequest')}
                            icon="pi pi-check"
                            size="small"
                            severity="success"
                            onClick={() => handleApprove(req)}
                            disabled={isProcessing}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Dialog
                visible={visible}
                onHide={onHide}
                header={th('vehiclesForSale.dialog.detailTitle')}
                style={{ width: '780px', maxWidth: '95vw' }}
                modal
                maximizable
            >
                {isFetching && (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                    </div>
                )}

                {!isFetching && detail && (
                    <>
                        <div className="flex justify-content-between align-items-start mb-3">
                            <div>
                                <div className="text-lg font-semibold">
                                    {detail.chassisNo}
                                </div>
                                <div className="text-sm text-600">
                                    {detail.brand} · {detail.model}
                                </div>
                            </div>
                            <Tag
                                value={detail.statusText}
                                severity={getListingStatusSeverity(detail.status)}
                            />
                        </div>

                        <div className="text-base font-medium mb-2">
                            {th('vehiclesForSale.labels.vehicleInfo')}
                        </div>

                        <div className="grid mb-3">
                            {infoRow(th('vehiclesForSale.columns.matnr'), detail.matnr)}
                            {infoRow(th('vehiclesForSale.columns.maktx'), detail.maktx)}
                            {infoRow(th('vehiclesForSale.columns.sellerDealer'), detail.sellerBranchName)}
                            {infoRow(th('vehiclesForSale.columns.listingDate'), formatDateTime(detail.createdDate))}
                            {infoRow(th('vehiclesForSale.columns.createdBy'), detail.createdName)}
                            {infoRow(th('vehiclesForSale.columns.description'), detail.description)}
                        </div>

                        {Array.isArray(detail.requests) && detail.requests.length > 0 && (
                            <>
                                <div className="text-base font-medium mb-2">
                                    {th('vehiclesForSale.labels.requests')}
                                </div>
                                <div>{detail.requests.map(renderRequestCard)}</div>
                            </>
                        )}
                    </>
                )}
            </Dialog>

            <RejectRequestDialog
                visible={!!rejectTarget}
                request={rejectTarget}
                onHide={handleRejectClose}
                onSuccess={handleRejectSuccess}
            />
        </>
    );
};

export default ListingDetailDialog;
